import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Avatar,
  InputAdornment,
  Chip,
  Alert,
} from "@mui/material";

// Icons
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PercentIcon from "@mui/icons-material/Percent";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { postCustomer, getSalesPersons } from "../../helpers/api_helper";
import VATModal from "./vatModal";
import MapModal from "./mapAddress";

const MuiCustomerCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  document.title = t("Create Customer") + " | FlexFlow ERP";

  // Form state
  const [startDate, setStartDate] = useState(new Date());
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [discount, setDiscount] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [category, setCategory] = useState("");
  const [vatType, setVatType] = useState("24");
  const [vat, setVat] = useState("");
  const [social, setSocial] = useState("");
  const [profession, setProfession] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [salespersonList, setSalespersonList] = useState([]);
  const [salesperson, setSalesperson] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSalesPersons().then((res) => setSalespersonList(res || []));
  }, []);

  const setCompanyDetails = (compDetails) => {
    if (compDetails) {
      setVat(compDetails.basicInfo.afm);
      setName(compDetails.basicInfo.onomasia);
      setTaxOffice(compDetails.basicInfo.doy_descr);
      setCountry("Greece");
      setProfession(compDetails.firmActivities?.[0]?.firm_act_descr || "");
      setCity(compDetails.basicInfo.postal_area_description);
      setAddress(
        `${compDetails.basicInfo.postal_address}, ${compDetails.basicInfo.postal_address_no}, ${compDetails.basicInfo.postal_zip_code}`
      );
    }
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!tags.find((tag) => tag.text === tagInput.trim())) {
        setTags([...tags, { id: tagInput.trim(), text: tagInput.trim() }]);
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag.id !== tagToRemove));
  };

  const createCustomer = async () => {
    setIsSubmitting(true);
    setError(null);

    const payload = {
      resource: [
        {
          name: name,
          label: label,
          taxOffice: taxOffice,
          address: address,
          phone: phone,
          startdate: startDate,
          discount: discount,
          category: category,
          vatType: vatType,
          vat: vat,
          social: social,
          profession: profession,
          email: email,
          tags: JSON.stringify(tags),
          salespersons: salesperson,
          city: city,
          country: country,
        },
      ],
    };

    try {
      const res = await postCustomer(payload);
      if (res?.error) {
        setError(res.error.message || t("Failed to create customer"));
        setIsSubmitting(false);
      } else if (res?.resource) {
        navigate("/customers");
      }
    } catch (err) {
      setError(t("Failed to create customer"));
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: t("Customers"), path: "/customers" },
    { label: t("Create Customer") },
  ];

  const isFormValid = name.length >= 4;

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Section Card Component
  const SectionCard = ({ icon: Icon, title, children, iconColor = "#3b82f6", iconBg = "#eff6ff" }) => (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: iconBg, color: iconColor }}>
              <Icon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {title}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
        <Container maxWidth="lg">
          <MuiPageHeader
            title={t("Create Customer")}
            subtitle={t("Add a new customer to your database")}
            breadcrumbs={breadcrumbs}
            actions={
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/customers")}
                sx={{ borderRadius: 2 }}
              >
                {t("Back to List")}
              </Button>
            }
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left Column - Main Form */}
            <Grid item xs={12} lg={8}>
              {/* VAT Lookup Section */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "2px dashed #d1d5db",
                  mb: 3,
                  bgcolor: "#fefce8",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "#3b82f6", bgcolor: "#eff6ff" },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: "#3b82f6" }}>
                      <SearchIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="#111827">
                        {t("Quick Fill with VAT Number")}
                      </Typography>
                      <Typography variant="body2" color="#6b7280">
                        {t("Enter a VAT number to automatically fill company details from AADE")}
                      </Typography>
                    </Box>
                    <VATModal ValidData={setCompanyDetails} />
                  </Stack>
                </CardContent>
              </Card>

              {/* Basic Information */}
              <SectionCard icon={PersonIcon} title={t("Basic Information")}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Customer Name")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("Enter customer name")}
                      required
                      error={name.length > 0 && name.length < 4}
                      helperText={name.length > 0 && name.length < 4 ? t("Name must be at least 4 characters") : ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&.Mui-focused": {
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Company Name")}
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder={t("Enter company name")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Email")}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("Enter email address")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Phone")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t("Enter phone number")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Address Information */}
              <SectionCard icon={LocationOnIcon} title={t("Address")} iconColor="#10b981" iconBg="#d1fae5">
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <TextField
                        fullWidth
                        label={t("Street Address")}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder={t("Enter street address")}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                      <MapModal address={address} onAddressSelect={(a) => setAddress(a)} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("City")}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t("Enter city")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Country")}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder={t("Enter country")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Tax & Financial Information */}
              <SectionCard icon={ReceiptLongIcon} title={t("Tax & Financial")} iconColor="#f59e0b" iconBg="#fef3c7">
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("VAT Number")}
                      value={vat}
                      onChange={(e) => setVat(e.target.value)}
                      placeholder={t("Enter VAT number")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ReceiptLongIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Tax Office (DOY)")}
                      value={taxOffice}
                      onChange={(e) => setTaxOffice(e.target.value)}
                      placeholder={t("Enter tax office")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("VAT Type")}</InputLabel>
                      <Select
                        value={vatType}
                        label={t("VAT Type")}
                        onChange={(e) => setVatType(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="0">0%</MenuItem>
                        <MenuItem value="17">17%</MenuItem>
                        <MenuItem value="24">24%</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Default Discount")}
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      placeholder={t("Enter discount %")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PercentIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Business Information */}
              <SectionCard icon={WorkOutlineIcon} title={t("Business Details")} iconColor="#8b5cf6" iconBg="#ede9fe">
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Profession")}
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      placeholder={t("Enter profession")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkOutlineIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("Category")}</InputLabel>
                      <Select
                        value={category}
                        label={t("Category")}
                        onChange={(e) => setCategory(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">{t("Select category")}</MenuItem>
                        <MenuItem value="Category1">{t("Category 1")}</MenuItem>
                        <MenuItem value="Category2">{t("Category 2")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("Social / Website")}
                      value={social}
                      onChange={(e) => setSocial(e.target.value)}
                      placeholder={t("Enter social media or website")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* Right Column - Summary & Actions */}
            <Grid item xs={12} lg={4}>
              {/* Customer Preview Card */}
              <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3, position: "sticky", top: 24 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
                    <Typography variant="subtitle2" fontWeight={600} color="#374151">
                      {t("Customer Preview")}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    {/* Avatar & Name */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: name ? "#3b82f6" : "#e5e7eb",
                          fontSize: "1.25rem",
                          fontWeight: 600,
                        }}
                      >
                        {name ? name.charAt(0).toUpperCase() : <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} color={name ? "#111827" : "#9ca3af"}>
                          {name || t("Customer Name")}
                        </Typography>
                        <Typography variant="body2" color="#6b7280">
                          {label || t("Company Name")}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* Details */}
                    <Stack spacing={1.5}>
                      {email && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <EmailIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                          <Typography variant="body2" color="#374151">{email}</Typography>
                        </Stack>
                      )}
                      {phone && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <PhoneIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                          <Typography variant="body2" color="#374151">{phone}</Typography>
                        </Stack>
                      )}
                      {address && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <LocationOnIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                          <Typography variant="body2" color="#374151" sx={{ wordBreak: "break-word" }}>
                            {address}
                          </Typography>
                        </Stack>
                      )}
                      {vat && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <ReceiptLongIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                          <Typography variant="body2" color="#374151">{t("VAT")}: {vat}</Typography>
                        </Stack>
                      )}
                    </Stack>

                    {/* Category & Tags */}
                    {(category || tags.length > 0) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {category && (
                            <Chip
                              size="small"
                              label={category}
                              sx={{ bgcolor: "#eff6ff", color: "#3b82f6", fontWeight: 500 }}
                            />
                          )}
                          {tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              size="small"
                              label={tag.text}
                              sx={{ bgcolor: "#f3f4f6", color: "#374151" }}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Additional Settings Card */}
              <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
                    <Typography variant="subtitle2" fontWeight={600} color="#374151">
                      {t("Additional Settings")}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    <Stack spacing={2.5}>
                      {/* Salesperson */}
                      <FormControl fullWidth size="small">
                        <InputLabel>{t("Salesperson")}</InputLabel>
                        <Select
                          value={salesperson}
                          label={t("Salesperson")}
                          onChange={(e) => setSalesperson(e.target.value)}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">{t("Select salesperson")}</MenuItem>
                          {salespersonList?.map((person, i) => (
                            <MenuItem key={i} value={person.id}>
                              {person.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Creation Date */}
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label={t("Creation Date")}
                        value={formatDateForInput(startDate)}
                        onChange={(e) => setStartDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />

                      {/* Tags */}
                      <Box>
                        <TextField
                          fullWidth
                          size="small"
                          label={t("Tags")}
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleAddTag}
                          placeholder={t("Type and press Enter")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalOfferIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                        {tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                            {tags.map((tag) => (
                              <Chip
                                key={tag.id}
                                label={tag.text}
                                size="small"
                                onDelete={() => handleRemoveTag(tag.id)}
                                sx={{
                                  bgcolor: "#f3f4f6",
                                  "& .MuiChip-deleteIcon": { color: "#9ca3af" },
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={createCustomer}
                  disabled={!isFormValid || isSubmitting}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                    },
                  }}
                >
                  {isSubmitting ? t("Creating...") : t("Create Customer")}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<CloseIcon />}
                  onClick={() => navigate("/customers")}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor: "#d1d5db",
                    color: "#6b7280",
                    "&:hover": {
                      borderColor: "#9ca3af",
                      bgcolor: "#f9fafb",
                    },
                  }}
                >
                  {t("Cancel")}
                </Button>
              </Stack>

              {/* Help Text */}
              <Box sx={{ mt: 3, p: 2, bgcolor: "#f9fafb", borderRadius: 2, border: "1px solid #e5e7eb" }}>
                <Typography variant="caption" color="#6b7280" sx={{ display: "block", lineHeight: 1.6 }}>
                  <strong>{t("Tip")}:</strong> {t("Use the VAT lookup feature to automatically fill in company details from the Greek tax authority (AADE).")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
  );
};

export default MuiCustomerCreate;
